import IEAdapter from './IEAdapter';
import ISmimeAdapter from './ISmimeAdapter';
import ModernAdapter from './ModernAdapter';
import initializeSmimeStore from '../actions/initializeSmimeStore';
import onBrowserNotSupported from '../actions/onBrowserNotSupported';
import onExtensionInitializationFailed from '../actions/onExtensionInitializationFailed';
import SmimeResult from '../store/schema/SmimeResult';
import { SMIME_SETTINGS_NON_PII_FIELDS } from '../utils/constants';
import doesBrowserSupportModernExtension from '../utils/doesBrowserSupportModernExtension';
import getSmimeSettings from '../utils/getSmimeSettings';
import { isBrowserSupported } from '../utils/smimeUtils';
import pick from 'lodash-es/pick';
import { PerformanceDatapoint } from 'owa-analytics';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { trace } from 'owa-trace';

let smimeAdapterInstance: ISmimeAdapter = null;

function unloadExtension() {
    if (smimeAdapterInstance) {
        smimeAdapterInstance.disposeAsync();
        smimeAdapterInstance = null;
    }
}

/**
 * Initialize Modern browser(Chrome/Edge) or IE ActiveX extension with S/MIME settings
 */
async function initializeExtension(): Promise<void> {
    const datapoint = new PerformanceDatapoint('InitializeSmimeAdapter', { isCore: true });
    const smimeSettings = getSmimeSettings();
    const smimeSettingsString = JSON.stringify(smimeSettings);
    trace.info('S/MIME: settings passed to extension: ' + smimeSettingsString);
    if (isConsumer()) {
        trace.error(
            'SmimeTraceError: SmimeAdapter is not supposed to be initialized for consumers'
        );
    }
    datapoint.addCustomData({
        // Remove unnecessary or PII data
        smimeSettings: JSON.stringify({
            smimeSettings: pick(smimeSettings, SMIME_SETTINGS_NON_PII_FIELDS),
        }),
    });

    const initializeAsyncResponse = await smimeAdapterInstance.initializeAsync(smimeSettingsString);

    // Add listener to unload extension
    window.addEventListener('unload', unloadExtension);

    // If extension was found and initialized using settings then proceed with store initialization
    if (initializeAsyncResponse) {
        const smimeResult: SmimeResult = JSON.parse(initializeAsyncResponse);
        trace.info('S/MIME: Initialization Result: ' + JSON.stringify(smimeResult));
        // Replace '.' with '/' so that the version does not look like an IP address
        datapoint.addCustomData({ smimeResult: JSON.stringify(smimeResult).replace(/\./g, '/') });
        initializeSmimeStore(smimeResult);
    } else {
        datapoint.addCustomData({ smimeResult: null });
    }
    datapoint.end();
}

/**
 * Returns a promise containing instance of ISmimeAdapter after bootstrapping and initialization
 */
const getSmimeAdapter = async (): Promise<ISmimeAdapter> => {
    try {
        if (isBrowserSupported()) {
            if (!smimeAdapterInstance) {
                smimeAdapterInstance = doesBrowserSupportModernExtension()
                    ? ModernAdapter
                    : IEAdapter;
                await initializeExtension();
            }
        } else {
            onBrowserNotSupported();
        }
    } catch (e) {
        onExtensionInitializationFailed();
    }
    return smimeAdapterInstance;
};

export default getSmimeAdapter();
